import { ReactLenis } from '@studio-freight/react-lenis';
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ArrowRight, FileText, Cloud, Layers } from 'lucide-react';

const CampaignPage = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsMobile(window.innerWidth < 768);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const ScrollWrapper = ({ children }: { children: any }) => {
    return isMobile ? <>{children}</> : <ReactLenis root>{children}</ReactLenis>;
  };

  return (
    <ScrollWrapper>
      <div className="min-h-screen bg-black text-white selection:bg-primary/30 font-sans">
        
        {/* Updated Navbar - Matches LandingPage behavior */}
        <motion.nav
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b transition-all duration-300 ${isScrolled
            ? "bg-white/90 border-gray-200 shadow-sm py-3"
            : "bg-transparent border-transparent py-5"
            }`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
            <motion.a 
              href="/" 
              className="flex items-center gap-3"
              whileHover={{ scale: 1.02 }}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden bg-white/10 backdrop-blur-sm border border-white/10">
                <img src="/Gemini_Generated_Image_yp6tz1yp6tz1yp6t-removebg-preview.png" alt="Kuberya AI" className="w-8 h-8 object-contain" />
              </div>
              <span className={`text-xl font-bold transition-colors ${isScrolled ? "text-gray-900" : "text-white"}`}>
                AI <span className="text-primary">Evaluator</span>
              </span>
            </motion.a>
            <motion.button
              onClick={() => navigate('/login')}
              whileHover={{ scale: 1.05, boxShadow: "0 15px 40px -10px rgba(255, 106, 26, 0.5)" }}
              whileTap={{ scale: 0.98 }}
              className="px-6 py-2.5 bg-primary text-white font-semibold rounded-xl shadow-lg shadow-primary/30"
            >
              Get Started
            </motion.button>
          </div>
        </motion.nav>

        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center pt-24 pb-12 overflow-hidden bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-8 sm:gap-12 items-center relative z-10 w-full">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight text-white leading-[1.1] mb-6">
                Conduct Exams on Paper.
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-400 mt-2">
                  Let the Machine Do the Rest.
                </span>
              </h1>
              <p className="text-xl text-white/80 max-w-xl mb-10 leading-relaxed font-light">
                Your entire exam cycle — from paper to evaluated results — runs faster, cheaper, and with almost zero manual effort.
              </p>
              
              <div className="flex gap-3 sm:gap-4 flex-col sm:flex-row">
                <button
                  onClick={() => navigate('/login')}
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-primary text-white font-bold text-base sm:text-lg rounded-2xl shadow-xl shadow-primary/30 flex items-center justify-center gap-3 hover:scale-105 transition-transform"
                >
                  Start Evaluating <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative rounded-[2rem] overflow-hidden shadow-[0_0_50px_rgba(255,87,34,0.3)] border border-white/10 aspect-[4/3] bg-gray-900"
            >
              {/* Note: spaces in the URL should be encoded as %20 */}
              <video 
                src="/WhatsApp%20Video%202026-03-12%20at%209.25.21%20AM.mp4" 
                autoPlay 
                loop 
                muted 
                playsInline
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
          
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce text-white/50">
            <ChevronDown className="w-6 h-6" />
          </div>
        </section>

        {/* Content Section */}
        <section className="py-8 sm:py-12 md:py-24 bg-white text-gray-900 relative">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <span className="inline-block py-1 px-3 rounded-full bg-primary/10 text-primary font-semibold mb-3 text-xs tracking-wider uppercase">
                AUTOMATED PROCESS
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">Everything Handled Automatically</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
              {/* Box 1: Standard A4 Sheets */}
              <motion.div
                whileHover={{ y: -5 }}
                className="p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl bg-gray-50 border border-gray-100 shadow-sm hover:shadow-xl hover:border-primary/30 transition-all flex flex-col group overflow-hidden space-y-4 sm:space-y-6"
              >
                <div>
                  <h3 className="text-xl font-bold mb-3">Standard A4 Sheets</h3>
                  <p className="text-gray-500 leading-relaxed text-sm">Use any standard A4 paper. No changes needed to your existing exam process.</p>
                </div>
                
                {/* Mock UI: Paper Stack Illustration */}
                <div className="relative">
                  <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm transform group-hover:translate-y-[-10px] transition-transform duration-500">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-gray-900 line-clamp-1">Physics_Paper_Final.pdf</div>
                        <div className="text-[10px] text-gray-400">Standard A4 Format</div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                        <motion.div 
                          className="h-full bg-primary" 
                          initial={{ width: 0 }}
                          whileInView={{ width: "100%" }}
                          transition={{ duration: 1.5, delay: 0.5 }}
                        />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] text-gray-400">Recognition Accuracy</span>
                        <span className="text-[10px] font-bold text-primary">99.8%</span>
                      </div>
                    </div>
                  </div>
                  {/* Decorative Elements */}
                  <div className="absolute top-12 left-8 -right-4 h-40 bg-gray-100/50 rounded-2xl -z-10 rotate-3" />
                  <div className="absolute top-16 left-12 -right-8 h-40 bg-gray-100/30 rounded-2xl -z-20 rotate-6" />
                </div>
              </motion.div>

              {/* Box 2: Zero Manual Sorting */}
              <motion.div 
                whileHover={{ y: -5 }}
                className="p-8 rounded-3xl bg-gray-50 border border-gray-100 shadow-sm hover:shadow-xl hover:border-primary/30 transition-all flex flex-col group space-y-6"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-bold mb-2">Zero Manual Sorting</h3>
                    <p className="text-gray-500 leading-relaxed text-sm">The machine handles everything. No staff needed to sort or feed papers.</p>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Layers className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-xs font-bold text-gray-800">Auto-Sort Engine</span>
                    <span className="ml-auto px-2 py-0.5 rounded-full bg-green-50 text-[10px] font-semibold text-green-600">Active</span>
                  </div>
                  <div className="space-y-2">
                    {["Roll Number Detection", "Subject Identification", "Answer Sheet Grouping"].map((item, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                        <span className="text-[11px] text-gray-500">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Box 3: High-Speed Scanning */}
              <motion.div 
                whileHover={{ y: -5 }}
                className="p-8 rounded-3xl bg-gray-50 border border-gray-100 shadow-sm hover:shadow-xl hover:border-primary/30 transition-all flex flex-col group space-y-6"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-bold mb-2">High-Speed Scanning</h3>
                    <p className="text-gray-500 leading-relaxed text-sm">Scan 80 sheets per minute. Over 35,000 sheets daily.</p>
                  </div>
                </div>
                
                {/* Mock UI: Scanning Speed Meter */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white rounded-xl border border-gray-100 p-3 shadow-sm">
                    <div className="text-[10px] text-gray-400 mb-1">Sheets/Min</div>
                    <div className="text-xl font-bold text-gray-900 tabular-nums">80.0</div>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-100 p-3 shadow-sm">
                    <div className="text-[10px] text-gray-400 mb-1">Daily Cap</div>
                    <div className="text-xl font-bold text-blue-600 tabular-nums">35k+</div>
                  </div>
                </div>
              </motion.div>

              {/* Box 4: Direct To Cloud */}
              <motion.div
                whileHover={{ y: -5 }}
                className="p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl bg-gray-50 border border-gray-100 shadow-sm hover:shadow-xl hover:border-primary/30 transition-all flex flex-col group overflow-hidden space-y-4 sm:space-y-6"
              >
                <div>
                  <h3 className="text-xl font-bold mb-3">Direct To Cloud</h3>
                  <p className="text-gray-500 leading-relaxed text-sm">Files sync instantly to the cloud for immediate AI evaluation with zero errors.</p>
                </div>
                
                {/* Mock UI: Cloud Data Flow */}
                <div className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden relative">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-50 border border-gray-100">
                      <Cloud className="w-4 h-4 text-purple-600" />
                      <div className="text-[10px] font-medium text-gray-700">Syncing with AWS S3...</div>
                    </div>
                    
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                            <div className="text-[9px] text-gray-500">Node_Process_{i}.dat</div>
                          </div>
                          <div className="text-[9px] font-bold text-green-500">Live</div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="pt-2 border-t border-gray-200">
                      <div className="text-[10px] font-bold text-gray-900 mb-2">Network Status</div>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5, 2, 4, 3, 5, 2, 3].map((h, i) => (
                          <motion.div 
                            key={i} 
                            initial={{ height: 4 }}
                            animate={{ height: h * 4 }}
                            transition={{ repeat: Infinity, repeatType: "reverse", duration: 0.5, delay: i * 0.1 }}
                            className="flex-1 bg-purple-200 rounded-full"
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Box 5: Cost-Effective */}
              <motion.div 
                whileHover={{ y: -5 }}
                className="p-8 rounded-3xl bg-gray-50 border border-gray-100 shadow-sm hover:shadow-xl hover:border-primary/30 transition-all flex flex-col group space-y-6"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-bold mb-2">Cost-Effective</h3>
                    <p className="text-gray-500 leading-relaxed text-sm">Save on paper and logistics. One-time hardware investment with no recurring costs.</p>
                  </div>
                </div>
                
                {/* Mock UI: Savings Graph */}
                <div className="h-16 flex items-end gap-1">
                  {[20, 35, 25, 45, 60, 55, 80, 70, 95].map((h, i) => (
                    <motion.div 
                      key={i}
                      initial={{ height: 0 }}
                      whileInView={{ height: `${h}%` }}
                      transition={{ duration: 1, delay: i * 0.05 }}
                      className={`flex-1 rounded-t-sm ${i === 8 ? 'bg-primary' : 'bg-green-100'}`}
                    />
                  ))}
                </div>
              </motion.div>

            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-8 sm:py-12 md:py-24 bg-gradient-to-br from-primary via-orange-500 to-orange-600 text-center px-4 sm:px-6 relative overflow-hidden">
          <div className="max-w-4xl mx-auto text-white relative z-10">
            <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-6 tracking-tight">Experience Zero Manual Effort</h2>
            <p className="text-xl text-white/90 mb-10 leading-relaxed font-medium">
              The result: Your entire exam cycle — from paper to evaluated results — runs faster, cheaper, and with almost zero manual effort.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="px-6 sm:px-10 py-3 sm:py-4 bg-white text-primary font-bold text-base sm:text-lg rounded-2xl shadow-2xl hover:scale-105 transition-transform inline-flex items-center gap-2 group"
            >
              Get Started Now
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </section>

      </div>
    </ScrollWrapper>
  );
};

export default CampaignPage;
